// Base
import Image from 'next/image'
import { useState } from 'react'

// Components
import { Box, Flex, Text } from 'yard-utilities/components'
import { Input } from '@components/v25/input/Input'
import IconBox from '@components/helpers/icon-box/IconBox'
import BackButton from '@components/v21/back-button/BackButton'

// Assets
import HeroImage from '@images/v21/photos/location-bg.png'
import { CloseCirlce, LocationIcon } from 'icons/v21/Icons'

// Styles
import styles from './LocationPopup.module.sass'

// Interfaces
import { CityData } from '@sections/v21/location/LocationWrapper'

interface LocationPopupProps {
  location: string
  setLocation: (value: string) => void
  setSelection: ({ id, name }: { id: number; name: string }) => void
  setDisplayPopup: (value: boolean) => void
  cities: CityData[]
  popularCities: CityData[]
}

const LocationPopup = ({
  setLocation,
  setSelection,
  location,
  setDisplayPopup,
  cities,
  popularCities
}: LocationPopupProps) => {
  const [results, setResults] = useState<CityData[]>([])
  const [error, setError] = useState('')

  const selectOption = (id: number, name: string) => {
    setLocation(name)
    setSelection({
      id,
      name
    })
    setDisplayPopup(false)
  }

  const changeInput = (value: string) => {
    setLocation(value)

    const destinations = []

    for (let i = 0; i < cities.length; i++) {
      const city = cities[i]

      if (city.name.toLowerCase().includes(value.toLowerCase())) {
        destinations.push({
          id: city.id,
          name: city.name
        })
      }
    }

    if (value != '' && destinations.length == 0) {
      setError('No location found. Please, search another location.')
    }

    setResults(destinations)
  }

  const clearInput = () => {
    setLocation('')
    setSelection({ id: 0, name: '' })
    setResults([])
    setError('')
  }

  const endIcon = () => {
    if (location != '') {
      return (
        <IconBox
          size="md"
          onClick={clearInput}
          className={styles.clearInputIcon}
        >
          <CloseCirlce />
        </IconBox>
      )
    }
  }

  return (
    <Box padding={{ sm: [16, 16, 48, 16] }} className={styles.popup}>
      <BackButton onClick={() => setDisplayPopup(false)} />

      <Image
        src={HeroImage}
        fill
        sizes="(max-width: 768px) 600px, (max-width: 1200px) 800px, 1000px"
        priority
        placeholder="blur"
        alt="Location hero image"
        className={styles.image}
      />

      <div className={styles.content}>
        <Text variant="body-5" fontWeight={600} spacing={{ sm: 8 }}>
          Departure location
        </Text>

        <Flex flexDirection="column" rowGap={{ sm: 8 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <Input
              startIcon={<LocationIcon />}
              placeholder="Type your location"
              id="location"
              name="location"
              value={location}
              onChange={(e) => {
                changeInput(e.target.value)
              }}
              endIcon={endIcon()}
              autoFocus
              autoComplete="off"
            />
          </form>

          {results.length > 0 ? (
            <SelectionBox>
              {results.map((result, index) => (
                <SelectionLink
                  selectOption={() => {
                    selectOption(result.id, result.name)
                  }}
                  key={index}
                >
                  {result.name}
                </SelectionLink>
              ))}
            </SelectionBox>
          ) : (
            <SelectionBox>
              {error ? (
                <Box padding={{ sm: [8, 16] }}>
                  <Text variant="body-3" textColor="red">
                    {error}
                  </Text>
                </Box>
              ) : (
                <Flex flexDirection="column" rowGap={{ sm: 8 }}>
                  <div>
                    <SelectionTitle>Popular locations</SelectionTitle>

                    {popularCities.map((tag, index) => (
                      <SelectionLink
                        selectOption={() => {
                          selectOption(tag.id, tag.name)
                        }}
                        key={index}
                      >
                        🔥 {tag.name}
                      </SelectionLink>
                    ))}
                  </div>
                  <div>
                    <SelectionTitle>All locations A-Z</SelectionTitle>
                    {cities.map((city, index) => (
                      <SelectionLink
                        selectOption={() => {
                          selectOption(city.id, city.name)
                        }}
                        key={index}
                      >
                        {city.name}
                      </SelectionLink>
                    ))}
                  </div>
                </Flex>
              )}
            </SelectionBox>
          )}
        </Flex>
      </div>
    </Box>
  )
}

const SelectionBox = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      backgroundColor="white"
      border="grey-5"
      padding={{ sm: [8, 0] }}
      borderRadius={{ sm: [8] }}
      className={styles.selection}
    >
      {children}
    </Box>
  )
}

interface SelectionLinkProps {
  children: React.ReactNode
  selectOption: () => void
}

const SelectionLink = ({ children, selectOption }: SelectionLinkProps) => {
  return (
    <Box padding={{ sm: [8, 16] }} role="button" onClick={selectOption}>
      <Text variant="body-3">{children}</Text>
    </Box>
  )
}

const SelectionTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box padding={{ sm: [8, 16] }}>
      <Text variant="body-5" fontWeight={600} textColor="black-9">
        {children}
      </Text>
    </Box>
  )
}

export default LocationPopup
